import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import type { AccountStatus } from '@impulso/shared'
import { Bell, Building2, ChevronDown, CreditCard, Menu, Palette, Plus, Shield, Users, X } from 'lucide-react'
import { api, errorMessage } from '../lib/api'
import { getLastCompany, setLastCompany } from '../lib/last-company'
import { useSession } from '../lib/session'
import { CompanyProvider, useCompanyQuery } from '../lib/company'
import { ErrorState, Loading } from '../ui'
import { useToast } from '../ui/toast'
import { companyNav, companySettingsNav, type NavItem } from './nav'
import { slots } from './slots'
import BrandMark from '../features/agency/BrandMark'

/** Aviso por situação da conta. Alinhado ao servidor: readonly, suspended e canceled só deixam ler; past_due ainda deixa mexer. */
const STATUS_BANNER: Partial<Record<AccountStatus, { tone: 'amber' | 'red'; text: string; cta: string }>> = {
  past_due: { tone: 'amber', text: 'Há um pagamento pendente. Regularize para a conta não ficar somente leitura.', cta: 'Regularizar' },
  readonly: { tone: 'red', text: 'Pagamento pendente: a conta está em modo somente leitura (dá para ver tudo, mas não alterar) e o piloto automático foi pausado.', cta: 'Regularizar' },
  suspended: { tone: 'red', text: 'Conta suspensa por falta de pagamento: só dá para ver os dados e o piloto automático está pausado.', cta: 'Regularizar' },
  canceled: { tone: 'red', text: 'A assinatura foi cancelada: a conta está somente para leitura.', cta: 'Escolher plano' },
}

/** Casca do app autenticado: menu lateral (empresa ou conta), topo com troca de empresa e área de conteúdo. */
export default function AppLayout() {
  const { companyId } = useParams()
  const { me } = useSession()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const { pathname } = useLocation()
  const menuBtn = useRef<HTMLButtonElement>(null)
  const sidebar = useRef<HTMLElement>(null)
  const companyQ = useCompanyQuery(companyId)
  const company = companyQ.data

  const allCompanies = (me?.accounts ?? []).flatMap((a) => a.companies.map((c) => ({ ...c, accountName: a.name, accountId: a.id })))
  // Conta da empresa aberta; fora de uma empresa, a da última empresa usada (ou a primeira)
  const lastId = getLastCompany()
  const account = (company ? me?.accounts.find((a) => a.id === company.accountId) : me?.accounts.find((a) => a.companies.some((c) => c.id === lastId))) ?? me?.accounts[0]
  const can = (p?: string) => !p || !!company?.access.permissions.includes(p as never)

  useEffect(() => { if (company) setLastCompany(company.id) }, [company?.id])
  useEffect(() => {
    if (!menuOpen) return
    sidebar.current?.querySelector('a')?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setMenuOpen(false); menuBtn.current?.focus() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menuOpen])

  const SuggestionsBadge = slots.SuggestionsBadge
  const AssistantButton = slots.AssistantButton
  const NotificationsBell = slots.NotificationsBell

  const navLink = (to: string, label: string, Icon: React.ComponentType<{ size?: number }>, extra?: ReactNode, opts: { end?: boolean; sub?: boolean; active?: boolean } = {}) => {
    const inner = <><Icon size={opts.sub ? 16 : 18} />{label}{extra}</>
    // `active` força o estado (ex.: "Configurações" vale para todo config/* menos "Meus avisos")
    if (opts.active !== undefined) return <Link key={to} to={to} aria-current={opts.active ? 'page' : undefined} className={`nav-item ${opts.active ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>{inner}</Link>
    return (
      <NavLink key={to} to={to} end={opts.end} className={({ isActive }) => `nav-item ${opts.sub ? 'nav-sub' : ''} ${isActive ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>
        {inner}
      </NavLink>
    )
  }
  // Configurações cobre todo config/*, exceto "Meus avisos", que tem item próprio
  const inConfig = (id: string) => pathname.startsWith(`/app/${id}/config/`) && !pathname.startsWith(`/app/${id}/config/avisos`)

  const renderItem = (n: NavItem, base: string) => {
    if (!n.children) return navLink(`${base}/${n.to}`, n.label, n.icon, n.badge === 'suggestions' && SuggestionsBadge && company ? <SuggestionsBadge companyId={company.id} /> : null)
    return <NavGroup key={n.to} item={n} base={base} onNavigate={() => setMenuOpen(false)} renderChild={(c) => navLink(`${base}/${c.to}`, c.label, c.icon, null, { end: c.end, sub: true })} />
  }

  return (
    <div className="shell">
      <aside id="app-sidebar" ref={sidebar} className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <Link to="/app" className="logo">
          <BrandMark companyId={company?.id} />
        </Link>
        {company && (
          <>
            {can('company.edit_drafts') && (
              <NavLink to={`/app/${company.id}/criar`} className="btn primary block sidebar-cta" onClick={() => setMenuOpen(false)}>
                <Plus size={17} /> Criar anúncio
              </NavLink>
            )}
            {companyNav.filter((n) => can(n.perm)).map((n) => renderItem(n, `/app/${company.id}`))}
            <div className="nav-section">Esta empresa</div>
            {companySettingsNav.filter((n) => can(n.perm)).map((n) => navLink(`/app/${company.id}/${n.to}`, n.label, n.icon, null, n.to.startsWith('config/') ? { active: inConfig(company.id) } : {}))}
            {navLink(`/app/${company.id}/config/avisos`, 'Meus avisos', Bell)}
          </>
        )}
        {account && (account.permissions.length > 0) && (
          <>
            <div className="nav-section">{account.kind === 'agency' ? 'Agência' : 'Sua conta'}</div>
            {account.kind === 'agency' && navLink('/app/agencia', 'Visão dos clientes', Building2)}
            {account.permissions.includes('account.manage_companies') && navLink(`/app/conta/${account.id}/empresas`, 'Empresas', Building2)}
            {account.permissions.includes('account.manage_members') && navLink(`/app/conta/${account.id}/usuarios`, 'Usuários', Users)}
            {account.permissions.includes('account.billing') && navLink(`/app/conta/${account.id}/plano`, 'Plano e cobrança', CreditCard)}
            {account.kind === 'agency' && account.permissions.includes('account.branding') && navLink(`/app/conta/${account.id}/marca`, 'Marca da agência', Palette)}
          </>
        )}
        {me?.user.isStaff && navLink('/admin', 'Backoffice', Shield)}
      </aside>
      {menuOpen && <div className="drawer-backdrop" style={{ zIndex: 65 }} onClick={() => { setMenuOpen(false); menuBtn.current?.focus() }} />}

      <div className="main">
        <div className="banners">
          {account?.status === 'trial' && account.trialEndsAt && (
            <div className="banner purple"><span className="banner-text"><span className="hide-xs">Teste grátis até {new Date(account.trialEndsAt).toLocaleDateString('pt-BR')}.</span><span className="show-xs">Teste até {new Date(account.trialEndsAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span></span> <Link className="strong" to={`/app/conta/${account.id}/plano`}>Escolher plano</Link></div>
          )}
          {account && STATUS_BANNER[account.status] && (
            <div className={`banner ${STATUS_BANNER[account.status]!.tone}`}><span className="banner-text wrap">{STATUS_BANNER[account.status]!.text}</span> <Link className="strong" to={`/app/conta/${account.id}/plano`}>{STATUS_BANNER[account.status]!.cta}</Link></div>
          )}
          {me && !me.user.emailVerified && <VerifyEmailBanner email={me.user.email} />}
        </div>
        <header className="topbar">
          <button ref={menuBtn} className="icon-btn menu-btn" aria-label="Abrir menu" aria-expanded={menuOpen} aria-controls="app-sidebar" onClick={() => setMenuOpen(true)}><Menu size={18} /></button>
          {allCompanies.length > 0 && (
            <label className="biz-switch company-switch">
              <span className="avatar sm" style={{ background: '#ffe4ec', color: '#be185d' }}>{(company?.name ?? 'E').slice(0, 2).toUpperCase()}</span>
              <select aria-label="Trocar de empresa" value={company?.id ?? ''} onChange={(e) => navigate(`/app/${e.target.value}/inicio`)}>
                {!company && <option value="">Escolha uma empresa</option>}
                {allCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}{me && me.accounts.length > 1 ? ` · ${c.accountName}` : ''}</option>)}
              </select>
            </label>
          )}
          <div className="spacer" />
          {company && AssistantButton && <AssistantButton companyId={company.id} />}
          {NotificationsBell && <NotificationsBell />}
          <UserMenu />
        </header>
        <main className="content">
          {companyId && companyQ.isLoading && <Loading />}
          {companyId && companyQ.error && <ErrorState error={companyQ.error} onRetry={() => companyQ.refetch()} />}
          {companyId && company && (
            <CompanyProvider company={company}>
              <Outlet />
            </CompanyProvider>
          )}
          {!companyId && <Outlet />}
        </main>
      </div>
    </div>
  )
}

/** Item com submenu: abre sozinho quando a rota atual está dentro dele; a seta abre/fecha manualmente. */
function NavGroup({ item, base, renderChild, onNavigate }: { item: NavItem; base: string; renderChild: (c: NavItem) => ReactNode; onNavigate: () => void }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const inside = pathname === `${base}/${item.to}` || pathname.startsWith(`${base}/${item.to}/`)
  const [manual, setManual] = useState<boolean | null>(null)
  useEffect(() => setManual(null), [inside])
  const open = manual ?? inside
  const Icon = item.icon
  return (
    <div className="nav-group">
      <div className={`nav-item nav-parent ${inside ? 'active' : ''}`}>
        <button type="button" className="nav-parent-link" onClick={() => { setManual(true); onNavigate(); navigate(`${base}/${item.to}`) }}>
          <Icon size={18} />
          {item.label}
        </button>
        <button type="button" className="nav-caret" aria-expanded={open} aria-label={open ? `Fechar ${item.label}` : `Abrir ${item.label}`} onClick={() => setManual(!open)}>
          <ChevronDown size={15} style={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }} />
        </button>
      </div>
      {open && <div className="nav-children">{item.children!.map(renderChild)}</div>}
    </div>
  )
}

/** Aviso discreto de e-mail não confirmado; some até a próxima visita se a pessoa dispensar. */
function VerifyEmailBanner({ email }: { email: string }) {
  const toast = useToast()
  const [hidden, setHidden] = useState(() => { try { return sessionStorage.getItem('impulso:aviso-email') === '1' } catch { return false } })
  const [busy, setBusy] = useState(false)
  if (hidden) return null
  const resend = async () => {
    setBusy(true)
    try { await api.post('/auth/resend-verification'); toast(`Enviamos um novo link para ${email}.`) } catch (err) { toast(errorMessage(err)) } finally { setBusy(false) }
  }
  const dismiss = () => { setHidden(true); try { sessionStorage.setItem('impulso:aviso-email', '1') } catch { /* sem armazenamento: some só desta vez */ } }
  return (
    <div className="banner muted" role="status">
      <span className="banner-text">
        <span className="hide-xs">Confirme seu e-mail ({email}) para receber avisos e recuperar sua senha.</span>
        <span className="show-xs">Confirme e-mail</span>
      </span>
      <button type="button" className="link" disabled={busy} onClick={resend}>Reenviar<span className="hide-xs"> link</span></button>
      <button type="button" className="link" onClick={dismiss} aria-label="Dispensar aviso"><span className="hide-xs">Agora não</span><X className="show-xs" size={14} aria-hidden /></button>
    </div>
  )
}

/** Menu do usuário: abre/fecha por clique ou toque, fecha com Esc ou ao clicar fora (o foco volta ao botão). */
function UserMenu() {
  const { me } = useSession()
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const initials = (me?.user.name ?? '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); btn.current?.focus() } }
    const onDown = (e: PointerEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown) }
  }, [open])
  return (
    <div style={{ position: 'relative' }} ref={wrap}>
      <button ref={btn} className="avatar" style={{ border: 'none' }} aria-label="Menu do usuário" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>{initials}</button>
      {open && (
        <div className="card user-menu" role="menu">
          <div className="small muted" style={{ padding: '6px 10px' }}>{me?.user.email}</div>
          <Link className="nav-item" role="menuitem" to="/app/perfil" onClick={() => setOpen(false)}>Meu perfil e segurança</Link>
          <Link className="nav-item" role="menuitem" to="/sair" onClick={() => setOpen(false)}>Sair</Link>
        </div>
      )}
    </div>
  )
}
